// Cria o PDF do CPCV no próprio browser, a partir do mesmo HTML que a app já gera,
// e devolve o ficheiro pronto a ver e a seguir em anexo no email.
// As bibliotecas só são carregadas quando se pede o PDF, para não pesar na app.

const LARGURA_PX = 794; // A4 a 96 dpi
const MARGEM_MM = 12;
const ESCALA = 2;

// Corrige a folha de estilos para a página ter só margens laterais (as de cima e de baixo são postas na paginação)
const AJUSTE_CSS = "<style>body{margin:0;padding:0}.page{width:" + LARGURA_PX + "px !important;max-width:none !important;padding:0 10mm !important;margin:0 !important}.secao,.subsecao{break-inside:avoid}</style>";

function criarIframe(html) {
  return new Promise((resolve, reject) => {
    const f = document.createElement("iframe");
    f.setAttribute("aria-hidden", "true");
    f.style.cssText = "position:fixed;left:-10000px;top:0;width:" + LARGURA_PX + "px;height:1123px;border:0;visibility:hidden";
    f.onload = () => resolve(f);
    f.onerror = () => reject(new Error("Não foi possível preparar o PDF."));
    f.srcdoc = html.replace("</head>", AJUSTE_CSS + "</head>");
    document.body.appendChild(f);
  });
}

// Posições (em px do documento) onde se pode cortar a página sem partir um bloco ao meio
function pontosDeCorte(doc) {
  const topo = doc.body.getBoundingClientRect().top;
  // um título de secção nunca fica sozinho no fim de uma página: não se corta entre o título e o primeiro bloco
  const aposTitulo = (e) => !!e.previousElementSibling && e.previousElementSibling.classList.contains("secao-titulo");
  const tops = (sel) => Array.from(doc.querySelectorAll(sel)).filter((e) => !aposTitulo(e)).map((e) => Math.round(e.getBoundingClientRect().top - topo)).sort((a, b) => a - b);
  return { grandes: tops(".secao, .subsecao, .assinatura-bloco, .rodape"), pequenos: tops(".grid2, .grid3, .campo") };
}

function escolherCorte(inicio, limite, pontos) {
  const dentro = (lista, minimo) => lista.filter((y) => y > inicio + minimo && y <= limite).pop();
  const altura = limite - inicio;
  return dentro(pontos.grandes, altura * 0.5) || dentro(pontos.pequenos, altura * 0.3) || limite;
}

export async function htmlParaPdf(html, { titulo = "CPCV" } = {}) {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
  const iframe = await criarIframe(html);
  try {
    const doc = iframe.contentDocument;
    if (doc.fonts && doc.fonts.ready) await doc.fonts.ready;
    const corpo = doc.body;
    const alturaTotal = Math.ceil(corpo.scrollHeight);
    const canvas = await html2canvas(corpo, { scale: ESCALA, backgroundColor: "#ffffff", useCORS: true, width: LARGURA_PX, windowWidth: LARGURA_PX, height: alturaTotal, windowHeight: alturaTotal });
    const pontos = pontosDeCorte(doc);

    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait", compress: true });
    pdf.setProperties({ title: titulo });
    const larguraMm = 210;
    const mmPorPx = larguraMm / LARGURA_PX; // 1 px do documento em mm
    const alturaPaginaPx = Math.floor((297 - 2 * MARGEM_MM) / mmPorPx); // altura útil de cada página, em px do documento

    // 1.º passo: onde corta cada página
    const cortes = [];
    let inicio = 0;
    while (inicio < alturaTotal - 1) {
      const limite = Math.min(inicio + alturaPaginaPx, alturaTotal);
      const fim = limite >= alturaTotal ? alturaTotal : escolherCorte(inicio, limite, pontos);
      cortes.push([inicio, fim]);
      inicio = fim;
    }

    // 2.º passo: desenha cada fatia numa página
    cortes.forEach(([de, ate], i) => {
      if (i > 0) pdf.addPage();
      const fatia = document.createElement("canvas");
      fatia.width = canvas.width;
      fatia.height = Math.max(1, Math.round((ate - de) * ESCALA));
      const ctx = fatia.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, fatia.width, fatia.height);
      ctx.drawImage(canvas, 0, Math.round(de * ESCALA), canvas.width, fatia.height, 0, 0, canvas.width, fatia.height);
      pdf.addImage(fatia.toDataURL("image/jpeg", 0.92), "JPEG", 0, MARGEM_MM, larguraMm, (ate - de) * mmPorPx, undefined, "FAST");
      pdf.setFontSize(8);
      pdf.setTextColor(120, 128, 145);
      pdf.text("Página " + (i + 1) + " de " + cortes.length, larguraMm / 2, 297 - 6, { align: "center" });
    });

    const blob = pdf.output("blob");
    const dataUri = pdf.output("datauristring");
    const base64 = dataUri.slice(dataUri.indexOf("base64,") + 7);
    return { blob, base64, paginas: cortes.length, tamanhoKB: Math.round(blob.size / 1024) };
  } finally {
    iframe.remove();
  }
}
